import React from 'react';
import { Box, Typography, Paper, Checkbox, Button, styled, Grid, IconButton, Tooltip } from '@mui/material';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Asset360Modal from './Asset360Modal';
import { useAssetsContext } from '../../context/AssetsContext';
import { useAssetTableLogic } from '../../hooks/assets/useAssetTableLogic';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFilesAsync } from '../../services/FileDownloader';
import AssetStatusTag from './AssetStatusTag';
import { getMissingCameraPositions, getMissingCameraTooltip } from '../../utils/missingCameraPositions';
import { getDirectionalThumbnail } from '../../utils/assetImages';
import { useSession } from '../../context/SessionContext';

const truncateVinDisplay = (value?: string) => {
  if (!value) return '';
  return value.length > 17 ? `${value.slice(0, 17)}...` : value;
};

const AssetCardList: React.FC = () => {
  const { assets } = useAssetsContext();
  const { systems } = useSession();

  const {
    openModal,
    modalAsset,
    thumbnailErrors,
    handleThumbnailError,
    handleRowClick,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    buildAssetDetailPath,
    isSelected,
  } = useAssetTableLogic();

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  return (
    <>
      {assets.map(asset => {
        const missingCameraPositions = getMissingCameraPositions(asset);
        const missingCameraTooltip = getMissingCameraTooltip(asset);
        const thumbnailSrc = getDirectionalThumbnail(asset, systems);

        return (
        <Paper key={asset.assetInstanceId} sx={{ mb: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* --- CARD HEADER (No Changes) --- */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {/* ... Asset ID and Checkbox */}
            <Box display="flex" alignItems="center">
              <Checkbox
                data-cy={`asset-card-checkbox-${asset.assetInstanceId}`}
                onClick={(e) => handleCheckboxClick(e, asset)}
                checked={isSelected(asset)}
                size="small"
                sx={{ '&.Mui-checked': { color: (theme) => theme.palette.status.accent }, p: 0, mr: 1 }}
              />
              <Tooltip title={asset.assetId}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, wordBreak: 'break-word', userSelect: 'text', cursor: 'text' }} onClick={(e) => e.stopPropagation()}>
                  {truncateVinDisplay(asset.assetId)}
                </Typography>
              </Tooltip>
            </Box>
            {/* ... Action Icons */}
            <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Copy Asset ID"><IconButton data-cy={`asset-card-copy-${asset.assetInstanceId}`} size="small" onClick={() => navigator.clipboard.writeText(asset.assetId)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
              <Tooltip title="Open in New Tab"><IconButton data-cy={`asset-card-newtab-${asset.assetInstanceId}`} size="small" component="a" href={buildAssetDetailPath(asset)} target="_blank" rel="noopener noreferrer"><OpenInNewIcon fontSize="inherit" /></IconButton></Tooltip>
              <Tooltip title="Download Images"><IconButton data-cy={`asset-card-download-${asset.assetInstanceId}`} size="small" onClick={() => downloadFilesAsync(asset)}><DownloadIcon fontSize="inherit" /></IconButton></Tooltip>
            </Box>
          </Box>

          {/* --- THUMBNAIL (No Changes) --- */}
          <Box data-cy={`asset-card-thumbnail-${asset.assetInstanceId}`} onClick={() => handleRowClick(asset)} sx={{ cursor: 'pointer', position: 'relative', width: '100%', paddingTop: '56.25%' }}>
            {thumbnailSrc && !thumbnailErrors[asset.assetInstanceId] ? (
              <Box component="img" src={thumbnailSrc} onError={() => handleThumbnailError(asset.assetInstanceId)} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
            ) : (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                <Typography>No Thumbnail</Typography>
              </Box>
            )}
          </Box>

          {/* --- CARD BODY (Updated Grid Layout) --- */}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Left Column: ALL Image Counts */}
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column">
                {[
                  { label: "Exterior", count: asset.imageExterior?.length ?? 0 },
                  { label: "Cropped", count: asset.imageCrop?.length ?? 0 },
                  { label: "Featured", count: asset.imageFeatured?.length ?? 0 },
                  { label: "Manual", count: asset.imageManual?.length ?? 0 },
                  { label: "Pano", count: asset.imagePano?.length ?? 0 },
                  { label: "UC", count: asset.imageUndercarriage?.length ?? 0 },
                  { label: "BGR", count: asset.imageBgr?.length ?? 0 },
                ].map(({ label, count }) => {
                  const hasMissingCameras = label === "Exterior" && missingCameraPositions.length > 0;
                  const countValue = (
                    <Typography variant="body2" sx={{ color: hasMissingCameras ? 'error.main' : 'text.primary' }}>
                      {count}
                    </Typography>
                  );

                  return (
                    <Box key={label} display="flex" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}:</Typography>
                      {hasMissingCameras ? (
                        <Tooltip title={missingCameraTooltip} arrow>
                          {countValue}
                        </Tooltip>
                      ) : countValue}
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            {/* Right Column: Status & Meta */}
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" gap={.5} alignItems="flex-start">
                {/* 👇 FIX #2: Stack the status tags vertically to prevent overflow */}
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>Processing:</Typography>
                  <AssetStatusTag 
                    flagValue={asset.processingProcessFlagValue} 
                    title={asset.processingError || undefined}
                  >
                    {asset.processingCurrentStep}
                  </AssetStatusTag>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>Syndication:</Typography>
                  <AssetStatusTag 
                    flagValue={asset.syndicationProcessFlagValue} 
                    title={asset.syndicationError || undefined}
                  >
                    {asset.syndicationCurrentStep}
                  </AssetStatusTag>
                </Box>

                <Typography variant="caption" sx={{ mt: .5 }}>
                  Posted: {new Date(+asset.created_at * 1000).toLocaleString()}
                </Typography>
                <Typography variant="caption">Config: {asset.config}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* 👇 FIX #1: Move the 360 logic OUTSIDE and AFTER the grid */}
          <Box sx={{ mt: 1, width: '100%' }}>
            {asset.imagePano?.length && (
              <SpinningButton
                data-cy={`asset-card-360-${asset.assetInstanceId}`}
                fullWidth // Make the button span the full width of the card
                onClick={(e) => handle360Click(e, asset.systemCode, asset.assetInstanceId)}
              >
                360 <SyncTwoToneIcon sx={{ fontSize: '14px', ml: 1 }} />
              </SpinningButton>
            )}
          </Box>
        </Paper>
        );
      })}

      <Asset360Modal
        open={openModal}
        handleClose={handleCloseModal}
        systemCode={modalAsset?.systemCode || null}
        assetInstanceId={modalAsset?.assetInstanceId || null}
      />
    </>
  );
};


export default AssetCardList;
